/*
 *
 * QrCodePopup
 *
 * @version 1.0.0
 * @author Marco Civello
 * @since 16-Jun-2025
 */

import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import styled from '@emotion/styled'
import { ArrowDownload, Copy } from '@nexidigital/iconlibrary'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalButton from '@nexidigital/portalbutton'
import PortalColorInput from '@nexidigital/portalcolorinput'
import PortalSelect from '@nexidigital/portalselect'
import PortalTextInput from '@nexidigital/portaltextinput'
import Toggle from 'Hoc/ToggleComponent'
import { exportDownload, getFeatureParams } from 'Utility/globalMethods'
import {
  QR_CODE_COLORS,
  QR_CODE_FORMATS,
  QR_CODE_PREVIEW_IMAGE,
  QR_CODE_VARIANTS
} from './constants'
import formattedMessage, { messages } from './languageModule'
import {
  copyQrCodeToClipboard,
  createColoredPreviewImage,
  createQrCodeBlob,
  findLegend,
  getVariantPreviewColor
} from './utils'
import './QrCodePopup.scss'

const PreviewText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'previewTextColor'
})(({ previewTextColor }) => ({
  color: `${previewTextColor} !important`,
  filter: 'none'
}))

const QrCodePopup = ({ actions, id, isLoading, qrCodeData, subscriptions, generatePayByLinkResult }) => {
  const intl = useIntl()
  // The action must be retained until the QR code data returns from the submit flow.
  const pendingActionRef = useRef(null)
  const [renderedPreviewImage, setRenderedPreviewImage] = useState(QR_CODE_PREVIEW_IMAGE)
  const [renderedSampleImage, setRenderedSampleImage] = useState(null)

  const sizeTypesData = [
    { value: 'SMALL', label: `300 x 300 ${intl.formatMessage(messages.pixels)}` },
    { value: 'MEDIUM', label: `600 x 600 ${intl.formatMessage(messages.pixels)}` },
    { value: 'LARGE', label: `1200 x 1200 ${intl.formatMessage(messages.pixels)}` }
  ]

  const shapeOptionsData = [
    { value: 'NONE', label: intl.formatMessage(messages.none) },
    { value: 'TRUCK', label: intl.formatMessage(messages.truck) },
    { value: 'SHOP', label: intl.formatMessage(messages.shopper) },
    { value: 'BAG', label: intl.formatMessage(messages.bag) },
    { value: 'CHEF', label: intl.formatMessage(messages.chef) }
    // { value: 'CART', label: 'Cart' },
  ]
  const hasDonaItaliaFeature =
    getFeatureParams(subscriptions, 'PayByLink', 'DonaItalia', 'Y', generatePayByLinkResult?.terminalId).length > 0
  const isDonaItalia = Boolean(generatePayByLinkResult?.donation || hasDonaItaliaFeature)
  const variant = isDonaItalia ? 'DONAITALIA' : 'STANDARD'
  const variantConfig = QR_CODE_VARIANTS[variant]

  const formik = useFormik({
    initialValues: {
      type: variant,
      imageFormat: 'PNG',
      size: 'MEDIUM',
      text: '',
      background: variantConfig.initialBackground,
      shape: variantConfig.initialShape,
      color: QR_CODE_COLORS.default
    },
    validationSchema: Yup.object({
      color: Yup.string().matches(/^#(?:[0-7][0-9A-F]){3}$/i)
    }),
    onSubmit: (values) => {
      if (typeof id !== 'string') return
      actions.doRetrievePblQrcode({ ...values, id: id.split('_')[0] })
    }
  })

  const comboBoxOptions = variantConfig.backgroundOptions.map(([image, text, value, message]) =>
    ({ image, text, value, alt: intl.formatMessage(message) })
  )

  const isWhitePreview = formik.values.background === variantConfig.whiteBackground
  const previewImage = variantConfig.previewImages[formik.values.shape]?.[formik.values.background] || QR_CODE_PREVIEW_IMAGE
  const previewColor = getVariantPreviewColor(variant, isWhitePreview, formik.values.color, QR_CODE_COLORS)
  const previewClasses = variantConfig.previewClasses[formik.values.shape] || {}
  const previewTextClassName = previewClasses.text || 'qrcode_previewContainer_qrCode_text'
  const previewSampleClassName = previewClasses.sample || 'qrcode_previewContainer_qrCode_sample'
  const shouldShowPreviewSample = variantConfig.alwaysShowSample || formik.values.shape !== 'NONE'
  const isPreviewWithoutShape = variantConfig.hasShapeSelect && formik.values.shape === 'NONE'
  const textLength = formik.values.text?.length || 0
  const selectedBackgroundLabel = comboBoxOptions.find(option => option.value === formik.values.background)?.text

  useEffect(() => {
    // Avoid state updates when the component unmounts while previews are being generated.
    let isMounted = true
    // Keep generated blob URLs so they can be released during cleanup.
    let objectUrls = []
    // DonaItalia keeps the main image unchanged; the standard variant recolors both images.
    const previewTasks = [
      variantConfig.useOriginalMainPreview ? previewImage : createColoredPreviewImage(previewImage, formik.values.color),
      createColoredPreviewImage(QR_CODE_PREVIEW_IMAGE, previewColor)
    ]

    // Keep the preview state consistent while generating the next images.
    if (variantConfig.useOriginalMainPreview) setRenderedPreviewImage(previewImage)
    else setRenderedSampleImage(null)

    const renderPreviews = async () => {
      // allSettled lets cleanup and fallback handling run even if only one image fails.
      const results = await Promise.allSettled(previewTasks)
      const failed = results.some(result => result.status === 'rejected')

      // The first DonaItalia result is the original asset, not a generated blob URL.
      const generatedUrls = results
        .filter((result, index) => result.status === 'fulfilled' && (!variantConfig.useOriginalMainPreview || index === 1))
        .map(result => result.value)

      if (failed || !isMounted) {
        generatedUrls.forEach(URL.revokeObjectURL)
        if (!isMounted) return

        // Fall back to the original assets if preview generation fails.
        if (!variantConfig.useOriginalMainPreview) setRenderedPreviewImage(previewImage)
        setRenderedSampleImage(QR_CODE_PREVIEW_IMAGE)
        return
      }

      const [preview, sample] = results.map(result => result.value)
      objectUrls = generatedUrls
      setRenderedPreviewImage(preview)
      setRenderedSampleImage(sample)
    }

    renderPreviews()

    return () => {
      // Release blob URLs from the previous effect to avoid memory leaks.
      isMounted = false
      objectUrls.forEach(URL.revokeObjectURL)
    }
  }, [formik.values.color, previewColor, previewImage, variantConfig.useOriginalMainPreview])

  const processImage = async () => {
    const format = formik.values.imageFormat
    const filename = `qrcode_${id}.${format.toLowerCase()}`

    try {
      const blob = createQrCodeBlob(format, qrCodeData)
      if (!blob) return

      const actionType = pendingActionRef.current

      if (actionType === 'copy') {
        const copied = await copyQrCodeToClipboard(format, qrCodeData, blob)
        if (!copied) return
      } else if (actionType === 'download') {
        exportDownload(blob, filename)
      }
      actions.closePopup()
      actions.setQrCodeToastMessage(
        actionType === 'copy'
          ? intl.formatMessage(messages.copySuccess)
          : intl.formatMessage(messages.downloadSuccess)
      )
    } catch (err) {
      console.error('Error processing image:', err)
    } finally {
      pendingActionRef.current = null
    }
  }

  useEffect(() => {
    if (!qrCodeData || !pendingActionRef.current) return
    processImage()
  }, [qrCodeData])

  useEffect(() => {
    document.body.classList.add('qrcodePopupOpen')
    return () => {
      document.body.classList.remove('qrcodePopupOpen')
    }
  }, [])

  useEffect(() => {
    formik.setFieldTouched('color', true)
  }, [formik.values.color])

  const handleShapeChange = option => {
    formik.setFieldValue('shape', option.value)
    if (option.value === 'NONE') formik.setFieldValue('text', '')
  }

  const handleFormatChange = option => {
    formik.setFieldValue('size', option.value === 'SVG' ? '' : 'MEDIUM')
    formik.setFieldValue('imageFormat', option.value)
  }

  const handleSizeChange = option => formik.setFieldValue('size', option.value)
  const handleBackgroundChange = value => formik.setFieldValue('background', value)
  const handleColorChange = event => formik.setFieldValue('color', event.target.value)

  const handleQrCodeAction = action => {
    pendingActionRef.current = action
    formik.handleSubmit()
  }

  return (
    <div className='qrcodePopup'>
      <Toggle>
        <MerchantModal
          id='qrcode_merchantmodal'
          title={intl.formatMessage(messages.title)}
          description={intl.formatMessage(messages.description)}
          closeLabel={intl.formatMessage(messages.closeAriaLabel)}
          onClose={() => actions.closePopup()}
          primaryButtonDisabled={!formik.isValid}
          secondaryButtonDisabled={!formik.isValid}
          primaryButtonLabel={intl.formatMessage(messages.download)}
          primaryIcon={<ArrowDownload size='20' type='line' />}
          primaryAction={() => handleQrCodeAction('download')}
          primaryButtonLoading={isLoading && pendingActionRef.current === 'download'}
          secondaryButtonLabel={intl.formatMessage(messages.copy)}
          secondaryIcon={<Copy size='20' type='line' />}
          secondaryAction={() => handleQrCodeAction('copy')}
          secondaryButtonLoading={isLoading && pendingActionRef.current === 'copy'}>
          <MerchantGenericCard>
            <div className='qrcode_main_container'>
              <div className='qrcode_left_container'>
                <div className='qrcode_optionContent'>
                  <div className='qrcode_optionContent_title_container'>
                    <h2 className='qrcode_optionContent_title'>1. {formattedMessage.aspect}</h2>
                    <p className='qrcode_optionContent_subtitle'>{variantConfig.aspectDescription}</p>
                  </div>

                  {variantConfig.hasShapeSelect && (
                    <div className='qrcode_shapeSelect'>
                      <PortalSelect
                        id='qrcode_shape'
                        label={intl.formatMessage(messages.shape)}
                        options={shapeOptionsData}
                        value={formik.values.shape}
                        onChange={handleShapeChange}
                        helperText={intl.formatMessage(messages.shapeHelperText)}
                        noSearch
                        placeholderBlank
                        ariaLabelIconButton={intl.formatMessage(messages.actionAriaLabel)}
                      />
                    </div>
                  )}

                  <div className='qrcode_comboChoice_container'>
                    <p className='qrcode_optionContent_subtitle'>{formattedMessage.colorsCombination}</p>
                    <div className='qrcode_buttonsWrapper'>
                      {comboBoxOptions.map((option) => (
                        <button
                          key={option.value}
                          type='button'
                          aria-pressed={formik.values.background === option.value}
                          className={`qrcode_buttonsWrapper_btn ${
                            formik.values.background === option.value ? 'selectedButton' : ''
                          }`}
                          onClick={() => handleBackgroundChange(option.value)}>
                          <img src={option.image} alt={option.alt} />
                          <span>{option.text}</span>
                        </button>
                      ))}
                    </div>
                    <p className='qrcode_optionContent_textcolor'>{formattedMessage.frameTextAutoColor}</p>
                  </div>
                  {variantConfig.hasCustomColor && (
                    <div className='qrcode_colorField'>
                      <PortalColorInput
                        id='qrcode_colorFrame'
                        label={intl.formatMessage(messages.color)}
                        value={formik.values.color}
                        onChange={handleColorChange}
                        helperText={intl.formatMessage(messages.colorHelperText)}
                        ariaLabelIconEdit={intl.formatMessage(messages.editColor)}
                        error={formik.errors.color && formik.touched.color}
                        errorText={intl.formatMessage(messages.colorHelperText)}
                      />
                    </div>
                  )}
                  <PortalTextInput
                    id='qrcode_textFrame'
                    label={formattedMessage.frameTextLabel}
                    ariaLabelInput={intl.formatMessage(messages.frameTextLabel)}
                    maxLength={34}
                    helperText={`${34 - textLength} ${intl.formatMessage(
                      textLength === 33 ? messages.remainingCharacter : messages.remainingCharacters
                    )}`}
                    value={formik.values.text}
                    onChange={(e) => formik.setFieldValue('text', e.target.value)}
                    disabled={formik.values.shape === 'NONE'}
                  />
                </div>

                <div className='qrcode_optionContent'>
                  <div className='qrcode_optionContent_title_container'>
                    <h2 className='qrcode_optionContent_title'>2. {formattedMessage.fileFormatTitle}</h2>
                    <p className='qrcode_optionContent_subtitle'>{formattedMessage.fileFormatDescription}</p>
                  </div>
                  <div className='qrcode_formatOptions'>
                    <div className='qrcode_selectWrapper'>
                      <PortalSelect
                        id='qrcode_format'
                        label={intl.formatMessage(messages.format)}
                        options={QR_CODE_FORMATS}
                        value={formik.values.imageFormat}
                        onChange={handleFormatChange}
                        noSearch
                        placeholderBlank
                        ariaLabelIconButton={intl.formatMessage(messages.actionAriaLabel)}
                      />
                      <PortalSelect
                        id='qrcode_size'
                        label={intl.formatMessage(messages.size)}
                        options={sizeTypesData}
                        value={formik.values.size}
                        onChange={handleSizeChange}
                        noSearch
                        onBlur={() => formik.setFieldTouched('size', true)}
                        placeholderBlank
                        disabled={formik.values.imageFormat === 'SVG'}
                        labelIcon='info'
                        infoToShow={[
                          {
                            title: intl.formatMessage(messages.tooltipTitle),
                            value: intl.formatMessage(messages.tooltipDescription300)
                          },
                          {
                            value: intl.formatMessage(messages.tooltipDescription600)
                          },
                          {
                            value: intl.formatMessage(messages.tooltipDescription1200)
                          }
                        ]}
                        ariaLabelIconButton={intl.formatMessage(messages.actionAriaLabel)}
                      />
                    </div>
                    <p className='qrcode_mandatory'>{intl.formatMessage(messages.mandatoryField)}</p>

                  </div>
                  <PortalButton
                    id='qrcode_resetButton'
                    label={formattedMessage.resetDefaultValues}
                    onClick={() => formik.resetForm()}
                    disabled={!formik.dirty}
                    size='small'
                    textButton
                  />
                </div>
              </div>
              <div className='qrcode_right_container'>
                <div className='qrcode_previewContainer_header'>
                  <h2 className='qrcode_previewContainer_title' id='qrcode_preview_title'>{formattedMessage.preview}</h2>
                  <p className='qrcode_previewContainer_subtitle'>{formattedMessage.disclaimerLine1}</p>
                  <p className='qrcode_previewContainer_subtitle'>{formattedMessage.disclaimerLine2}</p>
                </div>
                <div className='qrcode_previewContainer_qrCode'>
                  {shouldShowPreviewSample && renderedSampleImage && (
                    <img
                      src={renderedSampleImage}
                      alt={intl.formatMessage(messages.preview)}
                      className={previewSampleClassName}
                    />
                  )}
                  <img
                    src={renderedPreviewImage}
                    alt={intl.formatMessage(messages.preview)}
                    className={
                      variantConfig.useOriginalMainPreview
                        ? 'qrcode_previewContainer_qrCode_imageDonaItalia'
                        : isPreviewWithoutShape
                          ? 'qrcode_previewContainer_qrCode_imageNoShape'
                          : ''
                    }
                  />
                  <PreviewText className={previewTextClassName} previewTextColor={previewColor}>
                    {formik.values.text}
                  </PreviewText>
                </div>
                <div className='qrcode_previewContainer_details'>
                  <div>
                    <span className='qrcode_previewContainer_subtitle'>{formattedMessage.legendFrame}</span>
                    <span className='qrcode_previewContainer_subtitle'>{formattedMessage.legendCombination}</span>
                    <span className='qrcode_previewContainer_subtitle'>{formattedMessage.legendFormat}</span>
                    <span className='qrcode_previewContainer_subtitle'>{formattedMessage.legendSize}</span>
                  </div>
                  <div>
                    <span className='qrcode_previewContainer_previewValue'>
                      {findLegend(shapeOptionsData, formik.values.shape, intl.formatMessage(messages.heart))}
                    </span>
                    <span className='qrcode_previewContainer_previewValue'>
                      {selectedBackgroundLabel}
                    </span>
                    <span className='qrcode_previewContainer_previewValue'>
                      {findLegend(QR_CODE_FORMATS, formik.values.imageFormat)}
                    </span>
                    <span className='qrcode_previewContainer_previewValue'>
                      {findLegend(sizeTypesData, formik.values.size)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </MerchantGenericCard>
        </MerchantModal>
      </Toggle>
    </div>
  )
}

QrCodePopup.propTypes = {
  actions: PropTypes.object,
  id: PropTypes.string,
  isLoading: PropTypes.bool,
  qrCodeData: PropTypes.string,
  subscriptions: PropTypes.array,
  generatePayByLinkResult: PropTypes.object
}

QrCodePopup.defaultProps = {
  actions: {},
  id: '',
  isLoading: false,
  qrCodeData: null,
  subscriptions: [],
  generatePayByLinkResult: {}
}

export default QrCodePopup
